import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Container, Paper, Typography, Grid, Button, Tooltip, Snackbar, Alert } from '@mui/material';
import { AlertTriangle as AlertTriangleIcon } from 'lucide-react';

// Import separated components
import IssueFormFields from './issueformfield.jsx';
import IssueSidebar from './issuedata.jsx';
import FileUploadSection from './fileupload.jsx';
import HierarchySection from './hierarchy.jsx';
import { HierarchySelectionDialog, NewLabelDialog } from './dialougecomponent.jsx';

// Import hooks and utilities
import { useIssueForm } from './useissueform.jsx';
import { useFileUpload } from './useFileupload.jsx';
import { useHierarchy } from './usehierarchy.jsx';
import { useLabels } from './uselabel.jsx';
import { issueTypes } from './issuetype.jsx';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function CreateIssuePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const isEditMode = location.state?.editMode || false;
  const issueToEdit = location.state?.issueData || null;

  // NEW: Get selected project from localStorage or location state
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedProjectName, setSelectedProjectName] = useState('');

  useEffect(() => {
    // Check location state first
    if (location.state?.selectedProjectId) {
      setSelectedProjectId(location.state.selectedProjectId);
      setSelectedProjectName(location.state.selectedProjectName || '');
      localStorage.setItem('selectedProject', location.state.selectedProjectId);
      localStorage.setItem('selectedProjectName', location.state.selectedProjectName || '');
    } else {
      // Fallback to localStorage
      const savedProjectId = localStorage.getItem('selectedProject');
      const savedProjectName = localStorage.getItem('selectedProjectName');
      if (savedProjectId) {
        setSelectedProjectId(savedProjectId);
        setSelectedProjectName(savedProjectName || '');
      }
    }
  }, [location.state]);

  
  // Use ONLY the main form hook to avoid conflicts
  const {
    // State
    issueData,
    setIssueData,
    projects,
    teamMembers,
    labels,
    setLabels,
    selectedHierarchy,
    setSelectedHierarchy,
    projectHierarchy,
    epics,
    stories,
    tasks,
    uploadedFiles,
    setUploadedFiles,
    attachedFiles,
    setAttachedFiles,
    hierarchicalFiles,
    setHierarchicalFiles,
    groupedFiles,
    setGroupedFiles,
    errors,
    snackbar,
    openLabelDialog,
    setOpenLabelDialog,
    showHierarchyDialog,
    setShowHierarchyDialog,
    newLabel,
    setNewLabel,
    labelError,
    setLabelError,
    
    // Loading states
    isLoading,
    isTeamMembersLoading,
    isProductOwnerLoading,
    isLabelLoading,
    setIsLabelLoading,
    isFileUploading,
    setIsFileUploading,
    isEpicsLoading,
    isStoriesLoading,
    isTasksLoading,
    
    // Handlers
    handleChange,
    handleProjectChange,
    handleEpicChange,
    handleStoryChange,
    handleTaskChange,
    handleLabelsChange,
    handleSubmit,
    handleCloseSnackbar,
    showSnackbar,
    handleFileUpload,
    handleFileRemove,
    // Utilities
    createUploadPath,
    validateForm,
    ensureArray
  } = useIssueForm();

  // NEW: Auto-set project_id if selectedProjectId is available and issueData.project_id is not set
  useEffect(() => {
    if (selectedProjectId && !issueData.project_id && !isEditMode) {
      setIssueData(prev => ({
        ...prev,
        project_id: selectedProjectId,
        projectName: selectedProjectName // NEW: Store project name for display
      }));
      // Trigger project change to fetch epics/stories/tasks
      if (handleProjectChange) {
        handleProjectChange({
          target: { name: 'project_id', value: selectedProjectId }
        });
      }
      
    }
  }, [selectedProjectId, selectedProjectName, issueData.project_id, isEditMode, setIssueData, handleProjectChange, showSnackbar]);

  // Local state for file handling
  const [localUploadedFiles, setLocalUploadedFiles] = useState([]);

  // Enhanced API helper function
  const apiCall = async (endpoint, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Don't set Content-Type for FormData
    if (options.body instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || `HTTP ${response.status}: ${response.statusText}`;
        } catch {
          errorMessage = errorText || `HTTP ${response.status}: ${response.statusText}`;
        }
        
        throw new Error(errorMessage);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }
      
      return await response.text();
    } catch (error) {
      //console.error(`API call failed for ${endpoint}:`, error);
      throw error;
    }
  };

  // Mock file upload for development/testing
  const mockFileUpload = async (files, uploadContext = {}) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const mockFiles = Array.from(files).map(file => ({
          id: Date.now() + Math.random(),
          name: file.name,
          size: file.size,
          type: file.type,
          hierarchyPath: uploadContext.selectedHierarchy ? 
            `${uploadContext.selectedHierarchy.project}/${uploadContext.selectedHierarchy.epic || 'no-epic'}` : 
            'default',
          uploadedAt: new Date().toISOString(),
          path: `/uploads/${file.name}`,
          filename: file.name
        }));

        resolve({
          success: true,
          files: mockFiles,
          message: 'Files uploaded successfully (mock)'
        });
      }, 1000); // Simulate network delay
    });
  };

  // Label handlers (simplified versions)
  const handleOpenLabelDialog = () => {
    if (typeof setOpenLabelDialog === 'function') {
      setOpenLabelDialog(true);
    }
  };

  const handleCloseLabelDialog = () => {
    if (typeof setOpenLabelDialog === 'function') {
      setOpenLabelDialog(false);
    }
    if (typeof setNewLabel === 'function') {
      setNewLabel({ name: '', color: '#0052cc' });
    }
    if (typeof setLabelError === 'function') {
      setLabelError('');
    }
  };

  // Fixed new label change handler
  const handleNewLabelChange = (event) => {
    const { name, value } = event.target;
    if (typeof setNewLabel === 'function') {
      setNewLabel(prev => ({
        ...prev,
        [name]: value
      }));
    }
    if (typeof setLabelError === 'function') {
      setLabelError('');
    }
  };

  const handleCreateLabel = async () => {
    if (!newLabel?.name?.trim()) {
      if (typeof setLabelError === 'function') {
        setLabelError('Label name is required');
      }
      return;
    }
  
    try {
      if (typeof setIsLabelLoading === 'function') {
        setIsLabelLoading(true);
      }
  
      let newLabelData;
      try {
        // Extract company_id from token
        const token = localStorage.getItem('token');
        const result = await apiCall('/api/labels', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: newLabel.name.trim(),
            color: newLabel.color || '#0052cc'
          }),
        });
      
        newLabelData = result;
      } catch (error) {
        console.warn('Server label creation failed, creating locally:', error.message);
        // Fallback to local creation
        newLabelData = {
          id: Date.now(),
          name: newLabel.name.trim(),
          color: newLabel.color || '#0052cc'
        };
      }
  
      if (typeof setLabels === 'function') {
        setLabels(prev => [...(prev || []), newLabelData]);
      }
  
      handleCloseLabelDialog();
      if (typeof showSnackbar === 'function') {
        showSnackbar('Label created successfully!', 'success');
      }
    } catch (error) {
      //console.error('Label creation error:', error);
      if (typeof setLabelError === 'function') {
        setLabelError('Failed to create label');
      }
      if (typeof showSnackbar === 'function') {
        showSnackbar('Failed to create label', 'error');
      }
    } finally {
      if (typeof setIsLabelLoading === 'function') {
        setIsLabelLoading(false);
      }
    }
  };
  

  // Hierarchy file upload handler
  const handleHierarchicalFileUpload = (files) => {
    handleFileUpload(files, {
      selectedHierarchy,
      issueType: issueData.issueType,
      issueId: issueData.id
    });
  };

  // Initialize local uploaded files from the main hook
  useEffect(() => {
    if (uploadedFiles && Array.isArray(uploadedFiles)) {
      setLocalUploadedFiles(uploadedFiles);
    }
  }, [uploadedFiles]);


  // Get selected issue type for display
  const selectedIssueType = issueData?.issueType ? 
    issueTypes.find(type => type.id === parseInt(issueData.issueType)) : null;

  return (
    <Box className="flex-grow">
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar?.open || false}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar}
          severity={snackbar?.severity || 'info'}
          sx={{ width: '100%' }}
        >
          {snackbar?.message || ''}
        </Alert>
      </Snackbar>

      {/* Dialog Components */}
      <HierarchySelectionDialog
        open={showHierarchyDialog || false}
        onClose={() => setShowHierarchyDialog && setShowHierarchyDialog(false)}
        selectedHierarchy={selectedHierarchy}
        createUploadPath={createUploadPath || (() => '')}
        onFileUpload={handleHierarchicalFileUpload}
        issueData={issueData}
      />
      
      <NewLabelDialog
  open={openLabelDialog || false}
  onClose={handleCloseLabelDialog}
  newLabel={newLabel || { name: '', color: '#0052cc' }}
  onChange={handleNewLabelChange}  // Changed from onLabelChange
  error={labelError || ''}         // Changed from labelError
  isLoading={isLabelLoading || false} // Changed from isLabelLoading
  onCreate={handleCreateLabel}     // Changed from onCreateLabel
/>

<Container maxWidth="1500px" sx={{ py: 4 }}>
    <Paper elevation={3} sx={{ p: 4,  borderRadius: 2, // Rounded corners
  border: '2px solid',
  borderColor: 'grey.300', // Subtle professional border
  boxShadow: 4,  }}>
          <Typography variant="h4"  sx={{  fontWeight: 'bold', mb: 4 }}>
            {isEditMode ? 'Edit Requirements' : 'Requirements'}
          </Typography>
          
          <form onSubmit={handleSubmit}>
            <Grid container spacing={4}>
              {/* Left Column - Main Fields */}
              <Grid item xs={12} md={8}>
                <Grid container spacing={3}>
                  {/* Main Form Fields - Pass selectedProjectName and teamMembers */}
                  <IssueFormFields
                    issueData={issueData || {}}
                    errors={errors || {}}
                    projects={projects || []}
                    epics={epics || []}
                    stories={stories || []}
                    tasks={tasks || []}
                    isLoading={isLoading || false}
                    isEditMode={isEditMode}
                    isEpicsLoading={isEpicsLoading || false}
                    isStoriesLoading={isStoriesLoading || false}
                    isTasksLoading={isTasksLoading || false}
                    handleChange={handleChange}
                    handleProjectChange={handleProjectChange}
                    handleEpicChange={handleEpicChange}
                    handleStoryChange={handleStoryChange}
                    handleTaskChange={handleTaskChange}
                    selectedProjectName={selectedProjectName}
                    teamMembers={teamMembers || []}
                  />

                  {/* Hierarchy Section - This is the separate component for hierarchy */}
                  <HierarchySection
                    selectedHierarchy={selectedHierarchy}
                    issueData={issueData || {}}
                    epics={epics || []}
                    stories={stories || []}
                    tasks={tasks || []}
                    isEpicsLoading={isEpicsLoading || false}
                    isStoriesLoading={isStoriesLoading || false}
                    isTasksLoading={isTasksLoading || false}
                    handleEpicChange={handleEpicChange}
                    handleStoryChange={handleStoryChange}
                    handleTaskChange={handleTaskChange}
                    createUploadPath={createUploadPath || (() => '')}
                  />

                  {/* File Upload Section */}
                  <FileUploadSection
                    uploadedFiles={uploadedFiles}
                    isFileUploading={isFileUploading || false}
                    createUploadPath={createUploadPath || (() => '')}
                    handleFileUpload={handleFileUpload}
                    handleRemoveFile={handleFileRemove}
                    setShowHierarchyDialog={setShowHierarchyDialog}
                    selectedHierarchy={selectedHierarchy}
                    issueType={issueData?.issueType}
                    issueId={issueData?.id}
                    setIsFileUploading={setIsFileUploading}
                  />
                </Grid>
              </Grid>
              
              {/* Right Column - Sidebar Fields */}
              <Grid item xs={12} md={4}>
                <IssueSidebar
                  issueData={issueData || {}}
                  selectedIssueType={selectedIssueType}
                  teamMembers={teamMembers || []}
                  labels={labels || []}
                  errors={errors || {}}
                  isLoading={isLoading || false}
                  isTeamMembersLoading={isTeamMembersLoading || false}
                  isProductOwnerLoading={isProductOwnerLoading || false}
                  handleChange={handleChange}
                  handleLabelsChange={handleLabelsChange}
                  handleOpenLabelDialog={handleOpenLabelDialog}
                />
              </Grid>

              {/* Form Actions */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate(-1)}
                    disabled={isLoading || false}
                  >
                    Cancel
                  </Button>
                  
                 

<Tooltip 
  title={
    isLoading ? 'Processing...' : 
    (() => {
      const issueTypeId = parseInt(issueData.issueType);
      if (issueTypeId === 3 && !issueData.epic_id) return 'Please select an Epic (required for Story)';
      if (issueTypeId === 4 && !issueData.story_id) return 'Please select a Story (required for Task)';
      if (issueTypeId === 1 && !issueData.task_id) return 'Please select a Task (required for Bug)';
      return '';
    })()
  }
>
  <span>
    <Button
      type="submit"
      variant="contained"
      color="primary"
      disabled={
        isLoading || 
        (parseInt(issueData.issueType) === 3 && !issueData.epic_id) ||
        (parseInt(issueData.issueType) === 4 && !issueData.story_id) ||
        (parseInt(issueData.issueType) === 1 && !issueData.task_id)
      }
      startIcon={isLoading ? null : selectedIssueType?.icon || <AlertTriangleIcon />}
    >
      {isLoading ? 'Processing...' : isEditMode ? 'Update Issue' : 'Create Issue'}
    </Button>
  </span>
</Tooltip>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}